// Leaderboard Types
// Backend API: GET /api/v1/leaderboard

export type UserTier = 'diamond' | 'platinum' | 'gold' | 'silver' | 'bronze' | 'seedling';
export type LeaderboardCategory =
  | 'most_trades'
  | 'most_cards_sent'
  | 'longest_streak'
  | 'most_points'
  | 'points_sent'
  | 'biggest_trade'
  | 'trade_partners'
  | 'highest_rated'
  | 'new_members'
  | 'rising_stars';
export type LeaderboardGroup = 'volume' | 'value' | 'community' | 'newcomers';
export type LeaderboardPeriod = 'week' | 'month' | 'all_time';

// Tier thresholds (based on all-time trades)
export const TIER_THRESHOLDS: Record<UserTier, { min: number; max: number | null }> = {
  diamond: { min: 100, max: null },
  platinum: { min: 75, max: 99 },
  gold: { min: 50, max: 74 },
  silver: { min: 25, max: 49 },
  bronze: { min: 10, max: 24 },
  seedling: { min: 0, max: 9 },
};

// Tier display configuration
export const TIER_CONFIG: Record<UserTier, { label: string; color: string }> = {
  diamond: { label: 'Diamond', color: '#b9f2ff' },
  platinum: { label: 'Platinum', color: '#e5e4e2' },
  gold: { label: 'Gold', color: '#ffd700' },
  silver: { label: 'Silver', color: '#c0c0c0' },
  bronze: { label: 'Bronze', color: '#cd7f32' },
  seedling: { label: 'Seedling', color: '#90EE90' },
};

// Category display configuration. `description` is the line under the board
// pills (final copy, ticket 1219223211143266 Decision 9).
export const CATEGORY_CONFIG: Record<
  LeaderboardCategory,
  { label: string; statLabel: string; description: string }
> = {
  most_trades: {
    label: 'Most Trades',
    statLabel: 'trades',
    description: 'Completed trades, confirmed by both traders.',
  },
  most_cards_sent: {
    label: 'Cards Sent',
    statLabel: 'cards',
    description: 'Cards shipped and confirmed received by the other trader.',
  },
  longest_streak: {
    label: 'Longest Streak',
    statLabel: 'weeks',
    description: 'Most weeks in a row with at least one completed trade.',
  },
  most_points: {
    label: 'Most Points',
    statLabel: 'points',
    description: 'Current points balance. Only traders with an active want list are included.',
  },
  points_sent: {
    label: 'Points Sent',
    statLabel: 'points sent',
    description: 'Total points value of all cards a trader has sent.',
  },
  biggest_trade: {
    label: 'Biggest Trade',
    statLabel: 'points',
    description: 'Highest-value single trades. Both traders share the spot.',
  },
  trade_partners: {
    label: 'Trade Partners',
    statLabel: 'partners',
    description: 'Number of different traders they have completed a trade with.',
  },
  highest_rated: {
    label: 'Highest Rated',
    statLabel: 'rating',
    description: 'Average rating from trade partners. Minimum 10 reviews.',
  },
  new_members: {
    label: 'New Members',
    statLabel: 'wants',
    description: 'The newest traders on TradeMagic.',
  },
  rising_stars: {
    label: 'Rising Stars',
    statLabel: 'growth',
    description: 'Largest increase in trades compared with the previous period.',
  },
};

/** Group tabs and their boards, in display order (Decision 1). */
export const LEADERBOARD_GROUPS: ReadonlyArray<{
  key: LeaderboardGroup;
  label: string;
  boards: readonly LeaderboardCategory[];
}> = [
  { key: 'volume', label: 'Volume', boards: ['most_trades', 'most_cards_sent', 'longest_streak'] },
  { key: 'value', label: 'Value', boards: ['most_points', 'points_sent', 'biggest_trade'] },
  { key: 'community', label: 'Community', boards: ['trade_partners', 'highest_rated'] },
  { key: 'newcomers', label: 'Newcomers', boards: ['new_members', 'rising_stars'] },
];

export const ALL_CATEGORIES: readonly LeaderboardCategory[] = LEADERBOARD_GROUPS.flatMap((g) => g.boards);

export const isLeaderboardCategory = (value: unknown): value is LeaderboardCategory =>
  typeof value === 'string' && (ALL_CATEGORIES as readonly string[]).includes(value);

export const groupForCategory = (category: LeaderboardCategory): LeaderboardGroup =>
  (LEADERBOARD_GROUPS.find((g) => g.boards.includes(category)) ?? LEADERBOARD_GROUPS[0]).key;

/**
 * Boards that ignore the period: the toggle is hidden and requests send
 * all_time. rising_stars is here because the backend always compares the last
 * 30 days with the 30 before (review ruling, LeaderboardService.ts:603-741).
 */
export const PERIODLESS_CATEGORIES: readonly LeaderboardCategory[] = [
  'most_points',
  'longest_streak',
  'highest_rated',
  'rising_stars',
];

export const isPeriodless = (category: LeaderboardCategory): boolean =>
  PERIODLESS_CATEGORIES.includes(category);

/** Boards that carry the NEW tag until NEW_BOARDS_TAG_UNTIL. */
export const NEW_BOARDS: readonly LeaderboardCategory[] = [
  'longest_streak',
  'points_sent',
  'biggest_trade',
  'trade_partners',
];

/**
 * 30 days after the expected production deploy (2026-10-13). Correct this
 * literal in a follow-up commit if the deploy date slips.
 */
export const NEW_BOARDS_TAG_UNTIL = Date.parse('2026-11-12T00:00:00Z');

export const isNewBoard = (category: LeaderboardCategory, nowMs: number = Date.now()): boolean =>
  NEW_BOARDS.includes(category) && nowMs < NEW_BOARDS_TAG_UNTIL;

// Period display configuration
export const PERIOD_CONFIG: Record<LeaderboardPeriod, { label: string }> = {
  week: { label: 'This Week' },
  month: { label: 'This Month' },
  all_time: { label: 'All Time' },
};

// Individual leaderboard entry from backend
export interface LeaderboardEntry {
  rank: number;
  id: number;
  /** The wire also carries userId, and id came back null on the real response (2026-09-28); the Home card reads userId first. */
  userId?: number;
  username: string;
  avatarUrl: string | null;
  country: string | null;
  tier: UserTier;
  /** Completed trades, all-time. Optional: a response without it shows the join month alone. */
  totalTrades?: number;
  displayValue: number;
  points: number;
  memberSince: string;
  wantListCount?: number; // Populated for new_members and most_points categories
  isElite?: boolean;
  showAnimatedBorder?: boolean;
  borderColor1?: string | null;
  borderColor2?: string | null;
  /** highest_rated only. */
  reviewCount?: number;
  /**
   * biggest_trade only: the entry is a TRADE, not a user. cardsA is the entry
   * user's side. On a multi-leg offer `id` is the leg with the latest
   * completedAt; it is a React key only, never a link or a URL.
   */
  trade?: LeaderboardTradeInfo;
}

export interface LeaderboardTradeInfo {
  id: number;
  partner: {
    userId: number;
    username: string;
    avatarUrl: string | null;
    country: string | null;
    isElite: boolean;
  };
  cardsA: number;
  cardsB: number;
  completedAt: string;
}

// Current user's rank info (for "Your Rank" callout)
export interface CurrentUserRank {
  rank: number | null;
  tier: UserTier;
  displayValue: number;
  /** The entry one rank above the viewer in the same category and period; null at rank 1 or unranked. Additive (homepage card). */
  above?: { username: string; displayValue: number } | null;
}

// API response structure
export interface LeaderboardResponse {
  podium: LeaderboardEntry[];
  rankings: LeaderboardEntry[];
  currentUser: CurrentUserRank | null;
  totalCount: number;
  hasMore: boolean;
  page: number;
  limit: number;
}

// Query parameters for API
export interface LeaderboardQueryParams {
  category: LeaderboardCategory;
  period: LeaderboardPeriod;
  country?: string;
  search?: string;
  page?: number;
  limit?: number;
  minWantListCount?: number;
}

/** GET /leaderboard/standing (auth required): the viewer on every board in a group. */
export interface LeaderboardStandingBoard {
  category: LeaderboardCategory;
  rank: number | null;
  displayValue: number;
  above: { username: string; displayValue: number } | null;
  /** new_members only. */
  memberSince?: string;
}

export interface LeaderboardStandingResponse {
  group: LeaderboardGroup;
  period: LeaderboardPeriod;
  tier: UserTier;
  boards: LeaderboardStandingBoard[];
}

export type LeaderboardRecordCategory = 'biggest_trade' | 'longest_streak' | 'trade_partners' | 'points_sent';

/** GET /leaderboard/records (public): the all-time #1 on four boards. */
export interface LeaderboardRecordsResponse {
  records: { category: LeaderboardRecordCategory; entry: LeaderboardEntry }[];
}
